import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  LINK_SETS,
  LINK_SET_LABELS,
  ICON_NAMES,
  FALLBACK_TEXT,
  type LinkItem,
} from "@/lib/site-content";
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/content")({
  head: () => ({
    meta: [
      { title: "Admin | Site content | Scott Cunningham" },
      { name: "description", content: "Site content for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Site content | Scott Cunningham" },
      { property: "og:description", content: "Site content for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Site content | Scott Cunningham" },
      { name: "twitter:description", content: "Site content for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminContentPage,
});

type Tab = "links" | "text";

function AdminContentPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("links");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">Not authorized.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <AdminNav title="Site content" />
      <p className="text-sm text-muted-foreground">
        Edit the copy and reorderable link sets used across the site. Blog posts are managed under
        Posts.
      </p>

      <div className="mt-6 flex gap-2 border-b border-border">
        <TabButton active={tab === "links"} onClick={() => setTab("links")}>
          Link sets
        </TabButton>
        <TabButton active={tab === "text"} onClick={() => setTab("text")}>
          Text
        </TabButton>
      </div>

      <div className="mt-6">{tab === "links" ? <LinkSetsPanel /> : <TextPanel />}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

/* ============================================================
 *  LINK SETS
 * ============================================================ */

function LinkSetsPanel() {
  const [setKey, setSetKey] = useState<string>(LINK_SETS[0]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        {LINK_SETS.map((k) => (
          <button
            key={k}
            onClick={() => setSetKey(k)}
            className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
              setKey === k
                ? "border-foreground bg-foreground text-background"
                : "border-border hover:border-foreground"
            }`}
          >
            {LINK_SET_LABELS[k]}
          </button>
        ))}
      </div>
      <LinkSetEditor setKey={setKey} />
    </div>
  );
}

function LinkSetEditor({ setKey }: { setKey: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin_link_set", setKey],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("content_link_sets")
        .select("*")
        .eq("set_key", setKey)
        .order("position", { ascending: true });
      if (error) throw error;
      return (data ?? []) as LinkItem[];
    },
  });

  const items = data ?? [];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin_link_set", setKey] });
    qc.invalidateQueries({ queryKey: ["content_link_sets", setKey] });
  };

  async function saveItem(item: LinkItem) {
    const { error } = await (supabase as any)
      .from("content_link_sets")
      .update({
        label: item.label,
        href: item.href,
        sub: item.sub || null,
        icon: item.icon || null,
        color_var: item.color_var || null,
        external: item.external,
        enabled: item.enabled,
      })
      .eq("id", item.id!);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    invalidate();
  }

  async function deleteItem(id: string) {
    if (!confirm("Delete this link?")) return;
    const { error } = await (supabase as any).from("content_link_sets").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    invalidate();
  }

  async function move(index: number, dir: -1 | 1) {
    const other = items[index + dir];
    const cur = items[index];
    if (!other || !cur) return;
    await (supabase as any)
      .from("content_link_sets")
      .update({ position: other.position })
      .eq("id", cur.id);
    await (supabase as any)
      .from("content_link_sets")
      .update({ position: cur.position })
      .eq("id", other.id);
    invalidate();
  }

  async function addNew() {
    const item_key = prompt("Unique key (letters/numbers/underscore):");
    if (!item_key) return;
    const label = prompt("Label:") ?? "New link";
    const href = prompt("URL or path:") ?? "/";
    const nextPos = (items[items.length - 1]?.position ?? -1) + 1;
    const { error } = await (supabase as any).from("content_link_sets").insert({
      set_key: setKey,
      item_key,
      label,
      href,
      position: nextPos,
      external: href.startsWith("http") || href.startsWith("mailto:"),
    });
    if (error) return toast.error(error.message);
    invalidate();
  }

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button size="sm" onClick={addNew}>
          <Plus className="mr-1 h-4 w-4" /> Add link
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <LinkRow
            key={item.id}
            item={item}
            first={i === 0}
            last={i === items.length - 1}
            onSave={saveItem}
            onDelete={() => deleteItem(item.id!)}
            onUp={() => move(i, -1)}
            onDown={() => move(i, 1)}
          />
        ))}
        {items.length === 0 && (
          <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
            No links yet.
          </div>
        )}
      </div>
    </div>
  );
}

function LinkRow({
  item,
  first,
  last,
  onSave,
  onDelete,
  onUp,
  onDown,
}: {
  item: LinkItem;
  first: boolean;
  last: boolean;
  onSave: (i: LinkItem) => void;
  onDelete: () => void;
  onUp: () => void;
  onDown: () => void;
}) {
  const [draft, setDraft] = useState<LinkItem>(item);
  useEffect(() => setDraft(item), [item]);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(item), [draft, item]);
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="grid gap-3 md:grid-cols-6">
        <div className="md:col-span-1">
          <Label className="text-xs">Key</Label>
          <Input value={draft.item_key} disabled className="mt-1 font-mono text-xs" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Label</Label>
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">URL / path</Label>
          <Input
            value={draft.href}
            onChange={(e) => setDraft({ ...draft, href: e.target.value })}
            className="mt-1 font-mono text-xs"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Subtitle</Label>
          <Input
            value={draft.sub ?? ""}
            onChange={(e) => setDraft({ ...draft, sub: e.target.value })}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs">Icon</Label>
          <select
            value={draft.icon ?? ""}
            onChange={(e) => setDraft({ ...draft, icon: e.target.value || null })}
            className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
          >
            <option value="">(none)</option>
            {ICON_NAMES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Color (CSS var)</Label>
          <Input
            value={draft.color_var ?? ""}
            onChange={(e) => setDraft({ ...draft, color_var: e.target.value })}
            placeholder="var(--color-primary)"
            className="mt-1 font-mono text-xs"
          />
        </div>
        <div className="md:col-span-6 flex flex-wrap items-center gap-4 pt-1">
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={draft.external}
              onChange={(e) => setDraft({ ...draft, external: e.target.checked })}
            />
            Opens in new tab
          </label>
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            Enabled
          </label>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onUp} disabled={first}>
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={onDown} disabled={last}>
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => onSave(draft)} disabled={!dirty}>
              <Save className="mr-1 h-4 w-4" /> Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 *  TEXT
 * ============================================================ */

type TextRow = { id: string; text_key: string; value: string; note: string | null };

function TextPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin_content_text"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("content_text")
        .select("*")
        .order("text_key", { ascending: true });
      if (error) throw error;
      return (data ?? []) as TextRow[];
    },
  });

  const rows = data ?? [];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin_content_text"] });
    qc.invalidateQueries({ queryKey: ["content_text"] });
  };

  async function saveRow(row: TextRow) {
    const { error } = await (supabase as any)
      .from("content_text")
      .update({ value: row.value, note: row.note })
      .eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    invalidate();
  }

  async function resetRow(row: TextRow) {
    const def = FALLBACK_TEXT[row.text_key];
    if (def === undefined) return toast.error("No default available");
    await saveRow({ ...row, value: def });
  }

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <TextRowEditor key={row.id} row={row} onSave={saveRow} onReset={() => resetRow(row)} />
      ))}
      {rows.length === 0 && (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No text entries.
        </div>
      )}
    </div>
  );
}

function TextRowEditor({
  row,
  onSave,
  onReset,
}: {
  row: TextRow;
  onSave: (r: TextRow) => void;
  onReset: () => void;
}) {
  const [draft, setDraft] = useState<TextRow>(row);
  useEffect(() => setDraft(row), [row]);
  const dirty = draft.value !== row.value || (draft.note ?? "") !== (row.note ?? "");
  const hasDefault = FALLBACK_TEXT[row.text_key] !== undefined;
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="font-mono text-xs text-muted-foreground">{row.text_key}</div>
          {row.note && <div className="text-xs text-muted-foreground">{row.note}</div>}
        </div>
        <div className="flex gap-2">
          {hasDefault && (
            <Button size="sm" variant="outline" onClick={onReset}>
              Reset to default
            </Button>
          )}
          <Button size="sm" onClick={() => onSave(draft)} disabled={!dirty}>
            <Save className="mr-1 h-4 w-4" /> Save
          </Button>
        </div>
      </div>
      <Textarea
        value={draft.value}
        onChange={(e) => setDraft({ ...draft, value: e.target.value })}
        className="mt-3"
        rows={Math.min(8, Math.max(2, draft.value.split("\n").length + 1))}
      />
    </div>
  );
}